import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { IImage, IUploadRequestParams } from '../../../../../api/shared.dto';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { checkFileMaxWidthHeight, checkFileMinWidthHeight, isFileFormatValid, isFileSizeValid, replaceCommaAndSemicolon } from '../../../../../infrastructure/utils/common.utils';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import { IFileToS3DAO, UploadPathKey } from '../../../../../api/file-to-s3.dao';
import { IMAGE_VALIDATION_MAX_WIDTH_HEIGHT_ERROR_MESSAGE, IMAGE_VALIDATION_MIN_WIDTH_HEIGHT_ERROR_MESSAGE } from '../../../../../constants/validation-errors';
import {
    IMAGE_VALIDATION_FORMAT_ERROR_MESSAGE,
    IMAGE_VALIDATION_SIZE_ERROR_MESSAGE,
    IMAGE_VALIDATION_FORMAT_ERROR_MESSAGE_EXCEPT_SVG,
 } from '../../../../../constants/file.constants';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';

const defaultValues = {
    image: {
        title: '',
        objectKey: ''
    },
    imageWithUrl: {
        title: '',
        assetUrl: ''
    },
};

interface IImageData {
    file: File;
    minDimensions?: number;
    dimensions?: number;
    fieldName?: string;
}

export interface IDefaultImage {
    imageTitle: string;
    ImgSrc: string | (() => JSX.Element);
    showImageByDefault: boolean;
    defaultLogoToggleLabel: string;
    defaultLogoToggleLabelDisabled: string;
}

export default class UploadImageS3VM {
    @mobx.observable
    public image: IImage = null;

    @mobx.observable
    public file: File = null;

    @mobx.observable
    public isImageFormChecked = false;

    @mobx.observable
    public isImageUploading = false;

    @mobx.observable
    public imageError = '';

    @mobx.observable
    public showDefaultImage = false;

    @mobx.observable
    private imageChanged: boolean;

    protected uploadParams: IUploadRequestParams;

    constructor(
        protected readonly accountId: number,
        protected readonly eventId: number,
        protected readonly imageName: string,
        protected readonly pathKey: UploadPathKey, // to define path for uploading to s3 bucket
        readonly imageDescription?: string,
        readonly defaultImage?: IDefaultImage,
        public readonly exceptSVG?: boolean,
        @IoC.Inject private readonly fileToS3DAO: IFileToS3DAO = IoC.Container.get(IFileToS3DAO),
        @IoC.Inject protected readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) {
        this.uploadParams = { accountId: accountId, eventId: eventId };
        this.initializeImage();
        this.showDefaultImage = !!this?.defaultImage?.showImageByDefault;
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    @mobx.action
    public initializeImage = (
    ): void => {
        this.image = {
            assetUrl: defaultValues.imageWithUrl.assetUrl,
            title:  defaultValues.image.title,
            objectKey:  defaultValues.image.objectKey
        };
    };

    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @mobx.action
    public addImage = (url: string = '', title: string = '', objectKey: string = ''): void => {
        this.image = {
            assetUrl: url,
            title:  title,
            objectKey: objectKey
        };
    };

    @mobx.action
    public deleteImage = (): void => {
        this.initializeImage();
        this.file = null;
        this.imageChanged = true;
        this.imageError = '';
    };

    public getImagePreview = (): string | undefined => {
        if (this.file) {
            return URL.createObjectURL(this.file);
        }
    };

    public getImageName = (): string => {
        return this.file?.name ?? '';
    };

    @mobx.action
    public toggleShowDefaultImage = (value?: boolean): void => {
        if (typeof value === 'boolean') {
            this.showDefaultImage = value;
        } else {
            this.showDefaultImage = !this.showDefaultImage;
            if (this.showDefaultImage) {
                this.deleteImage();
            }
        }
    };

    public getImageToApi = () => {
        const isImageEmpty = Object.values(this.image).every(value => !value);
        if (isImageEmpty) {
            // Explicit remove sends blank objectKey; null means unchanged / no image on hub.
            return this.imageChanged
                ? { [this.imageName]: { title: '', objectKey: '' } }
                : { [this.imageName]: null };
        }
        return { [this.imageName]: { title: this.image.title, objectKey: this.image.objectKey } };
    };


    @mobx.action
    public allowToShowValidationErrors = (): void => {
        this.toggleImageFormChecked(true);
    };

    @mobx.action
    public setImage = async (imageData: IImageData): Promise<void> => {
        const {
            file,
            minDimensions = null,
            fieldName = '',
            dimensions = null
        } = imageData;
        this.imageError = '';

        if (!file) return;
        const updatedFile = this.pathKey === 'attendee'
            ? replaceCommaAndSemicolon(file)
            : file
        ;
        const withoutSVG = !!this.exceptSVG;
        const isCorrectFileType = await isFileFormatValid(updatedFile, withoutSVG);
        const isFileValidByMinDimensions = (minDimensions && isCorrectFileType) ? await checkFileMinWidthHeight(updatedFile, minDimensions) : false;
        const isFileValidByMaxDimensions = (dimensions && isCorrectFileType) ? await checkFileMaxWidthHeight(updatedFile, dimensions) : false;

        mobx.runInAction(() => {
            if (!isCorrectFileType) {
                this.allowToShowValidationErrors();
                this.imageError = fieldName + ' ' + `${
                    withoutSVG
                    ? IMAGE_VALIDATION_FORMAT_ERROR_MESSAGE_EXCEPT_SVG
                    : IMAGE_VALIDATION_FORMAT_ERROR_MESSAGE}`
                ;
                return;
            } else if (!isFileSizeValid(updatedFile)) {
                this.allowToShowValidationErrors();
                this.imageError = fieldName + ' ' + IMAGE_VALIDATION_SIZE_ERROR_MESSAGE;
                return;
            } else if (minDimensions && !isFileValidByMinDimensions) {
                this.allowToShowValidationErrors();
                this.imageError = fieldName + ' ' + IMAGE_VALIDATION_MIN_WIDTH_HEIGHT_ERROR_MESSAGE(minDimensions, this.intl);
                return;
            }
            else if (dimensions && !isFileValidByMaxDimensions) {
                this.allowToShowValidationErrors();
                this.imageError = fieldName + ' ' + IMAGE_VALIDATION_MAX_WIDTH_HEIGHT_ERROR_MESSAGE(dimensions, this.intl);
                return;
            }

            if (updatedFile) {
                this.file = updatedFile;
                this.uploadImageToS3();
            }
        });
    };

    @mobx.action
    public uploadImageToS3 = async (): Promise<void> => {
        try {
            this.isImageUploading = true;
            const response = await this.fileToS3DAO.uploadImageToS3(this.file, this.uploadParams, this.pathKey);
            mobx.runInAction(() => {
                this.addImage(response.assetUrl, this.file.name, response.objectKey);
                this.imageChanged = true;
            });
        }
        catch (e) {
            UploadImageS3VM.handleApiException(e);
            this.deleteImage();

        } finally {
            mobx.runInAction(() => {
                this.isImageUploading = false;
            });
        }
    };

    public get getImageChanged(): boolean | undefined {
        return this.imageChanged;
    }

    @mobx.action
    public setImageChanged = (imageChanged: boolean | undefined): void => {
        this.imageChanged = imageChanged;
    };

    @mobx.action
    public toggleImageFormChecked = (value: boolean): void => {
        this.isImageFormChecked = value;
    };

    @mobx.action
    public resetImage = (): void => {
        this.deleteImage();
        this.setImageChanged(undefined);
        this.toggleImageFormChecked(false);
    };
}